/**
 * Token Refresh Utilities
 * 
 * Provides proactive token refresh management to prevent token expiration
 * during active user sessions. Refreshes tokens before they expire (default: 5 minutes).
 * 
 * Phase 2: Proactive Token Refresh
 */

const DEFAULT_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes in milliseconds
const TOKEN_EXPIRATION_CHECK_INTERVAL = 60 * 1000; // Check every minute
const MAX_REFRESH_RETRIES = 3;
const REFRESH_RETRY_DELAY = 2000; // 2 seconds

let proactiveRefreshTimer = null;
let expirationCheckInterval = null;
let refreshRetryCount = 0;

/**
 * Calculate time until token expiration
 * @param {number} expirationTime - Token expiration timestamp in milliseconds
 * @returns {number} - Milliseconds until expiration
 */
export const getTimeUntilExpiration = (expirationTime) => {
  if (!expirationTime) {
    return null;
  }
  
  const now = Date.now();
  return Math.max(0, expirationTime - now);
};

/**
 * Check if token should be refreshed proactively
 * @param {number} expirationTime - Token expiration timestamp in milliseconds
 * @param {number} refreshBuffer - Milliseconds before expiration to refresh (default: 5 minutes)
 * @returns {boolean} - True if token should be refreshed
 */
export const shouldRefreshToken = (expirationTime, refreshBuffer = DEFAULT_REFRESH_BUFFER) => {
  if (!expirationTime) {
    return false;
  }
  
  const timeUntilExpiration = getTimeUntilExpiration(expirationTime);
  return timeUntilExpiration !== null && timeUntilExpiration <= refreshBuffer;
};

/**
 * Schedule proactive token refresh
 * @param {Function} refreshCallback - Function to call when token should be refreshed
 * @param {number} expirationTime - Token expiration timestamp in milliseconds
 * @param {number} refreshBuffer - Milliseconds before expiration to refresh (default: 5 minutes)
 */
export const scheduleProactiveRefresh = (
  refreshCallback,
  expirationTime,
  refreshBuffer = DEFAULT_REFRESH_BUFFER
) => {
  // Clear any existing timer
  clearProactiveRefresh();
  
  if (!expirationTime || !refreshCallback) {
    return;
  }
  
  const timeUntilExpiration = getTimeUntilExpiration(expirationTime);
  const timeUntilRefresh = Math.max(0, timeUntilExpiration - refreshBuffer);
  
  if (timeUntilRefresh <= 0) {
    // Token should be refreshed immediately
    refreshCallback();
    return;
  }
  
  // Schedule refresh before expiration
  proactiveRefreshTimer = setTimeout(() => {
    refreshCallback();
    proactiveRefreshTimer = null;
  }, timeUntilRefresh);
};

/**
 * Start monitoring token expiration and schedule proactive refresh
 * @param {Function} refreshCallback - Function to call when token should be refreshed
 * @param {number} expirationTime - Token expiration timestamp in milliseconds
 * @param {number} refreshBuffer - Milliseconds before expiration to refresh (default: 5 minutes)
 */
export const startExpirationMonitoring = (
  refreshCallback,
  expirationTime,
  refreshBuffer = DEFAULT_REFRESH_BUFFER
) => {
  // Clear any existing monitoring
  stopExpirationMonitoring();
  
  if (!expirationTime || !refreshCallback) {
    return;
  }
  
  // Check expiration periodically and schedule refresh
  expirationCheckInterval = setInterval(() => {
    if (shouldRefreshToken(expirationTime, refreshBuffer)) {
      // Schedule refresh if not already scheduled
      if (!proactiveRefreshTimer) {
        scheduleProactiveRefresh(refreshCallback, expirationTime, refreshBuffer);
      }
    }
  }, TOKEN_EXPIRATION_CHECK_INTERVAL);
  
  // Initial check
  if (shouldRefreshToken(expirationTime, refreshBuffer)) {
    scheduleProactiveRefresh(refreshCallback, expirationTime, refreshBuffer);
  }
};

/**
 * Stop monitoring token expiration
 */
export const stopExpirationMonitoring = () => {
  if (expirationCheckInterval) {
    clearInterval(expirationCheckInterval);
    expirationCheckInterval = null;
  }
  
  clearProactiveRefresh();
};

/**
 * Clear proactive refresh timer
 */
export const clearProactiveRefresh = () => {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
  
  refreshRetryCount = 0;
};

/**
 * Refresh token with retry logic
 * @param {Function} refreshFunction - Function that returns a Promise with new token
 * @param {number} maxRetries - Maximum number of retry attempts
 * @returns {Promise<string>} - The refreshed token
 */
export const refreshTokenWithRetry = async (
  refreshFunction,
  maxRetries = MAX_REFRESH_RETRIES
) => {
  let lastError = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const token = await refreshFunction();
      refreshRetryCount = 0;
      return token;
    } catch (error) {
      lastError = error;
      refreshRetryCount++;
      
      if (attempt < maxRetries) {
        // Exponential backoff for retries
        const delay = REFRESH_RETRY_DELAY * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
};

/**
 * Get token refresh status
 * @returns {Object} - Status object with timer and interval information
 */
export const getRefreshStatus = () => {
  return {
    hasScheduledRefresh: proactiveRefreshTimer !== null,
    hasActiveMonitoring: expirationCheckInterval !== null,
    retryCount: refreshRetryCount
  };
};

